import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { find, get } from 'lodash';

import { Grid, GridCell } from 'v1/components/shared';

import TableContext from './TableContext';
import styles from './Table.module.scss';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu.js';
import { Button } from 'v5/design-system/Button.js';
import { MoreHorizontal } from 'lucide-react';

const TableHeader = ({ groupId, row }) => {
  const {
    isLoading,
    headerGroups,
    frozenColumns,
    groupBy,
    groupOptions,
    prepareRow
  } = useContext(TableContext);

  const groupRow = find(
    get(row, 'subRows', []),
    row => get(row, 'original._type') === 'GROUP'
  );
  const groupName = get(groupRow, 'original._name');

  row && prepareRow(row);

  // GROUPING
  const renderExpand = () => (
    <GridCell width="auto" onClick={() => row.toggleRowExpanded()}>
      <img
        className={classnames(styles.GroupExpandIcon, 'clickable', {
          'rotate-90': row.isExpanded
        })}
        src="/images/icon_chevron_eggplant_lighter.svg"
        alt="Expand group"
      />
    </GridCell>
  );

  // GROUPING
  const renderOptions = () => {
    const flyoverWidth = groupOptions.find(opt => opt.description)
      ? 'S'
      : 'content';

    return (
      <div className="tailwind-root contents">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="xs"
              className="-m-[0.27rem]"
              layout="icon"
              variant="ghost"
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {groupOptions.map(option => (
              <DropdownMenuItem
                onClick={() => option.onClick(groupId)}
                disabled={option.disabled}
              >
                {option.children || (
                  <>
                    {option.icon && <img src={option.icon} alt="" />}
                    <div>
                      {option.title}
                      {option.description && (
                        <div className="text-fg-muted">
                          {option.description}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  // SORTING
  const renderSortIcon = column => (
    <div
      className={classnames([
        styles.SortIcon,
        {
          [styles.SortIcon_active]: column.isSorted
        }
      ])}
    >
      {column.isSortedDesc ? (
        <img src="/images/icon_column_sort_desc.svg" alt="" />
      ) : (
        <img src="/images/icon_column_sort_asc.svg" alt="" />
      )}
    </div>
  );

  const renderColumnHeader = column => {
    const rowGrouped = row && row.isGrouped;
    const groupCollapsed = rowGrouped && !row.isExpanded;
    const isIndexCell = column.id === '_index';
    const isGroupedCell = groupBy && column.index === 0;

    const sortingProps = column.isSortable
      ? column.getSortByToggleProps({
          title: column.isSortedDesc ? 'Sort descending' : 'Sort ascending',
          ...(isLoading ? { onClick: undefined } : {})
        })
      : {};

    const props = column.getHeaderProps([
      // SORTING
      sortingProps,
      // Standard
      {
        className: classnames([
          styles.Cell,
          column.className,
          {
            [styles.GroupCell]: isGroupedCell,
            [styles.Cell_bordered]:
              column.border === true && !groupCollapsed && !isIndexCell,
            [styles.Cell_noborder]:
              groupCollapsed || isIndexCell || column.border === false,
            [styles.Cell_sticky]: column.index < frozenColumns,
            [styles.Cell_sticky_last]: column.index === frozenColumns - 1,
            [styles.align_C]: column.align === 'center',
            [styles.align_R]: column.align === 'right'
          }
        ]),
        style: {
          left: column.index < frozenColumns ? column.totalLeft : null
        }
      }
    ]);

    return (
      <div {...props}>
        {isGroupedCell ? (
          <div>
            <Grid direction="row" gutters="S">
              <GridCell>
                <Grid gutters="S">
                  <GridCell>
                    <div className="trunc">{groupName}</div>
                  </GridCell>
                  {renderExpand()}
                </Grid>
              </GridCell>
              {groupOptions && (
                <GridCell width="auto">{renderOptions()}</GridCell>
              )}
            </Grid>
          </div>
        ) : isIndexCell ? (
          <>{row.index + 1}.0</>
        ) : (
          column.render('Header')
        )}
        {column.isSortable && renderSortIcon(column)}
      </div>
    );
  };

  return (
    <div className={styles.Header}>
      {headerGroups.map(headerGroup => (
        <div
          {...headerGroup.getHeaderGroupProps({
            className: styles.Row
          })}
        >
          {headerGroup.headers.map(column => renderColumnHeader(column))}
        </div>
      ))}
    </div>
  );
};

TableHeader.propTypes = {
  row: PropTypes.object
};

export default TableHeader;
